import React, { useEffect, useRef, useState } from "react";
import FormsEvidence from "./FormsEvidence";
import EvidenceTable from "./EvidenceTable";
import { ApplicationState } from "../../../../../store";
import { bindActionCreators, Dispatch } from "redux";
import { connect, useSelector } from "react-redux";
import {
  getCustomBehaviors,
  getDataPeriods,
  getEvidenceInDataPeriod,
  getMasterDataInFilter,
  getStaticDataCategoryDomains,
  openFileUploadErrorModal,
  resetEvidenceInDataPeriod,
  resetGetEvidenceDataPeriodAPIBlocked
} from "../../../../../store/onboarding/actions";
import { DataPeriod, EvidencePeriodData } from "../../../../../store/onboarding/types";
import LoadingIndicator from "../../LoadingIndicator";
import PickDataSourceModal from "./PickDataSourceModal";
import { getStudentInterventionGroups, hideGroupRecommendationFromData } from "../../../../../store/onboarding/cases/actions";
import GroupStudents from "../../third-step/group-students/GroupStudents";
import DataFiltersPanel from "./DataFiltersPanel";
import usePrevProps from "../../../../../utils/hooks/usePrevProps";
import FirstDataUploadTour from "../../../../pages/coach-dashboard/tours/FirstDataUploadTour";
import AddMoreData from "../../third-step/group-students/AddMoreData";
import { useLoading } from "../../../../../utils/hooks/useLoading";
import { LocalStorageKeys } from "../../../../../utils/LocalStorageUtils";
import useUserRole from "../../../../../utils/hooks/useUserRole";
import DataTablePagination from "./dataTablePagination";
import { resetFilterAction, resetPagination, resetSortAction } from "../../../../../store/dataFilter/actions";
import { toastr } from "react-redux-toastr";
import { getSkillFocuses } from "../../../../../store/groups/actions";

type PropsFromState = {
  errors: {
    getDataPeriods?: string;
    getEvidenceData?: string;
    uploadAssessmentData?: string | any[];
    uploadUndefinedAssessmentData?: string | any[];
    uploadEvidenceData?: string | any[];
    updateAssessment?: string | any[];
    createAssessment?: string | any[];
  };
  isLoading: {
    getDataPeriods: boolean;
    getEvidenceData: boolean;
    uploadAssessmentData: boolean;
    uploadUndefinedAssessmentData: boolean;
    uploadEvidenceData: boolean;
    updateDataPeriod: boolean;
    deleteDataPeriod: boolean;
    updateAssessment: boolean;
    createAssessment: boolean;
  };
  currentDataPeriod?: DataPeriod;
  hasEvidencePeriodData: boolean;
  isGetEvidenceDataPeriodAPIBlocked?: boolean;
};

type DispatchProps = {
  getDataPeriods: () => any;
  getStudentInterventionGroups: (dataPeriodId: number) => any;
  getStaticDataCategoryDomains: () => any;
  getCustomBehaviors: () => any;
  getEvidenceInDataPeriod: (dataPeriodId?: number) => any;
  getMasterDataInFilter: (dataPeriodId?: number) => any;
  hideGroupRecommendationFromData: () => void;
  resetPagination: () => any;
  resetFilterAction: () => any;
  resetEvidenceInDataPeriod: () => any;
  resetSortAction: () => any;
  resetGetEvidenceDataPeriodAPIBlocked: () => any;
  openFileUploadErrorModal: (message: any) => void;
  getSkillFocuses: () => any;
};

type OwnProps = {
  tableOnly?: boolean;
  onDataPeriodLoadingChange?: (loading: boolean) => void;
};

type DisplayedAssessementGroup = {
  dataPeriodId: number;
  evidenceColumnGroup: Array<{
    evidenceColumnGroupId: number;
    show: boolean;
  }>;
};
type Props = OwnProps & PropsFromState & DispatchProps;

const EvidenceInfoTab: React.FunctionComponent<Props> = (props) => {
  const prevProps = usePrevProps({
    getDataPeriods: props.isLoading.getDataPeriods,
    uploadAssessmentData: props.isLoading.uploadAssessmentData,
    uploadEvidenceData: props.isLoading.uploadEvidenceData,
    uploadUndefinedAssessmentData:
      props.isLoading.uploadUndefinedAssessmentData,
    dataPeriodId: props.currentDataPeriod?.id,
    deleteDataPeriod: props.isLoading.deleteDataPeriod,
    updateAssessment: props.isLoading.updateAssessment,
    createAssessment: props.isLoading.createAssessment
  });

  const [displayedGroups, setDisplayedGroup] = useState<DisplayedAssessementGroup>();
  // We fetch data periods first, then (once we know which period is active)
  // we fetch the evidence for it. There's a brief moment between those two
  // calls where neither "loading" flag is true yet - without help, the
  // skeleton would disappear and the old content would flash back during
  // that moment. These two refs let us "fill in" that gap so the skeleton
  // stays visible the whole time, right up until the evidence call starts.
  //
  // We have to set this flag directly while rendering (not inside a
  // useEffect) because effects run after the screen has already painted -
  // by then it's too late and the flash has already happened.
  //
  // Note: the condition below needs to match the one in the effect further
  // down that actually kicks off the evidence fetch. Otherwise we might miss
  // a refetch (e.g. when data periods finish loading again later) and get
  // the same flash we're trying to avoid.
  const lastHandledDataPeriodIdRef = useRef<number | undefined>(undefined);
  const isEvidenceFetchPendingRef = useRef(false);

  const willRefetchEvidence =
    !!props.currentDataPeriod?.id &&
    !props.isGetEvidenceDataPeriodAPIBlocked &&
    ((prevProps?.getDataPeriods !== props.isLoading.getDataPeriods) ||
      (prevProps?.dataPeriodId !== props.currentDataPeriod.id) ||
      lastHandledDataPeriodIdRef.current !== props.currentDataPeriod.id);

  if (willRefetchEvidence) {
    // We're about to kick off a fresh evidence fetch below - show the
    // skeleton now instead of waiting for the loading flag to catch up.
    isEvidenceFetchPendingRef.current = true;
  }
  if (props.isLoading.getEvidenceData) {
    // The fetch has actually started now, so we can stop relying on our
    // own flag and let the real loading state take over.
    isEvidenceFetchPendingRef.current = false;
  }
  const isEvidenceFetchPending = isEvidenceFetchPendingRef.current;

  const [evidencePeriodData, setEvidencePeriodData] = useState<any>();
  const originalEvidencePeriodData = useSelector<ApplicationState,EvidencePeriodData | undefined >(
    (s) => s.onboarding.evidencePeriodData);

  const { isDistrictOwner, isTeacher } = useUserRole();

   useEffect(() => {
      props.onDataPeriodLoadingChange?.(props.isLoading.getEvidenceData || props.isLoading.getDataPeriods || isEvidenceFetchPending);
    }, [props.isLoading.getEvidenceData, props.isLoading.getDataPeriods, isEvidenceFetchPending, props.onDataPeriodLoadingChange]);

  useEffect(() => {
    props.getDataPeriods();
    // props.getStaticDataCategoryDomains();
    // props.getCustomBehaviors();
    props.hideGroupRecommendationFromData();
    if(isDistrictOwner) {
      props.getSkillFocuses();
    }
  }, []);
  
  useEffect(() => {
    if (props.currentDataPeriod?.id) {
      if(prevProps?.dataPeriodId !== props.currentDataPeriod.id) {
        props.resetPagination();
        props.resetFilterAction();
        props.resetSortAction();
        props.resetEvidenceInDataPeriod();
      }
    }
  }, [props.currentDataPeriod?.id]);
  
  useEffect(() => {
    //RC-1994, we will prevent the evidence data period call so that pagination will not get distrupted
    // when any new data period is created, updated or deleted (except current data period)
    if (props.currentDataPeriod?.id && !props.isGetEvidenceDataPeriodAPIBlocked
    ) {
       if((prevProps?.getDataPeriods !== props.isLoading.getDataPeriods)
       || (prevProps?.dataPeriodId !== props.currentDataPeriod.id)
       ) {
          lastHandledDataPeriodIdRef.current = props.currentDataPeriod.id;
          props.getMasterDataInFilter(props.currentDataPeriod?.id);
          props.getEvidenceInDataPeriod(props.currentDataPeriod?.id);
          props.getStudentInterventionGroups(props.currentDataPeriod?.id);
        }
      }
  }, [props.currentDataPeriod]);

  const isDataPeriodOrEvidenceLoading =
    props.isLoading.getEvidenceData ||
    props.isLoading.getDataPeriods ||
    isEvidenceFetchPending;


  useEffect(() => {
    return () => {
      if(!location.pathname.includes('/data')) {
        props.resetPagination();
        props.resetFilterAction();
        props.resetSortAction();
        props.resetEvidenceInDataPeriod();
        props.resetGetEvidenceDataPeriodAPIBlocked();
      }
    }
  },[location.pathname])
  
  useLoading(
    props.isLoading.uploadAssessmentData,
    !(JSON.stringify(props.errors.uploadAssessmentData)?.includes('tab_name')) 
    ? props.errors.uploadAssessmentData : '',
    () => {
      if(!isDistrictOwner && (prevProps?.dataPeriodId !== props.currentDataPeriod?.id) && !props.errors.uploadAssessmentData) {
      return props.getDataPeriods()
      }
    },
    () => {
      if (!(JSON.stringify(props.errors.uploadAssessmentData)?.includes('tab_name')) ) {
        console.log(props.errors.uploadAssessmentData);
      }
    }
  );

  useEffect(() => {
    if(!prevProps?.updateAssessment && props.isLoading.updateAssessment) {
      if(!props.errors.updateAssessment) {
       toastr.success("Success", "Assessment updated successfully.");
      }
    }
  }, [props.errors.updateAssessment])

  useEffect(() => {
    if(!prevProps?.createAssessment && props.isLoading.createAssessment) {
      if(!props.errors.createAssessment) {
       toastr.success("Success", "Assessment created successfully.");
      }
    }
  }, [props.errors.createAssessment])

  
  useEffect(() => {
    if(prevProps?.uploadAssessmentData && !props.isLoading.uploadAssessmentData) {
      if(props.errors.uploadAssessmentData) {
        if(JSON.stringify(props.errors.uploadAssessmentData)?.includes('tab_name')) {
          props.openFileUploadErrorModal(props.errors.uploadAssessmentData)
        }
      }
    }
  }, [props.errors.uploadAssessmentData]);

  useEffect(() => {
    //console.log('displayedGroup', displayedGroups)
  },[displayedGroups])


  useEffect(()=> {
    if((originalEvidencePeriodData != undefined) 
    //&& originalEvidencePeriodData?.evidence_column_groups.length > 0
    ){
      const obj = {
        dataPeriodId: props.currentDataPeriod?.id!,
        evidenceColumnGroup: originalEvidencePeriodData?.evidence_column_groups,
        studentRows: originalEvidencePeriodData.student_rows,
        pagination: {
          totalRowCount: originalEvidencePeriodData.pagination?.total_row_count
        }
      }
      setEvidencePeriodData(obj)
      setDisplayedGroupsStorage(obj)
      .then((storageGroups: any) => {
        setDisplayedGroup(storageGroups);
      })
      .then(() => {
        setDisplayedGroup(getDisplayedGroupsFromStorage(obj))
      });
      
    }
  }, [originalEvidencePeriodData])


  useEffect(() => {
    if (
      ((prevProps?.uploadEvidenceData &&
        !props.isLoading.uploadEvidenceData &&
        !props.errors.uploadEvidenceData) ||
      (prevProps?.uploadUndefinedAssessmentData &&
        !props.isLoading.uploadUndefinedAssessmentData &&
        !props.errors.uploadUndefinedAssessmentData))
      && !isDistrictOwner
    ) {
      props.resetFilterAction();
      props.resetPagination();
      props.resetSortAction();
      props.getEvidenceInDataPeriod();
    }
  }, [
    prevProps?.uploadAssessmentData,
    prevProps?.uploadEvidenceData,
    prevProps?.uploadUndefinedAssessmentData,
    props.isLoading.uploadAssessmentData,
    props.isLoading.uploadEvidenceData,
    props.isLoading.uploadUndefinedAssessmentData,
    props.errors.uploadAssessmentData,
    props.errors.uploadEvidenceData,
    props.errors.uploadUndefinedAssessmentData,
    evidencePeriodData
  ]);

  const setDisplayedGroupsStorage =  (evidencePeriodData:any) => {
    const displayedGroupsData = localStorage.getItem(
      LocalStorageKeys.DisplayedGroups
    );
    let storageGroups = displayedGroupsData
      ? JSON.parse(displayedGroupsData)
      : [];

      if(!storageGroups.some((storageData:any)=> storageData.dataPeriodId == evidencePeriodData.dataPeriodId)){
        const displayedGroup = {
          dataPeriodId: evidencePeriodData.dataPeriodId,
          evidenceColumnGroup: (evidencePeriodData?.evidenceColumnGroup.length > 0) ? evidencePeriodData?.evidenceColumnGroup?.map(
            (evidenceGroup:any) => {
              return {
                evidenceColumnGroupId: evidenceGroup.id,
                show: true,
              }
            }) : []
        };
        storageGroups.push(displayedGroup);
      } else {
        storageGroups = [...storageGroups.map((storageGroup:any) => {
          const storageColumnGroupArray: any[] = storageGroup.evidenceColumnGroup.map((ecg:any) => ecg.evidenceColumnGroupId);
          if((storageGroup.dataPeriodId == evidencePeriodData.dataPeriodId)
          && !evidencePeriodData?.evidenceColumnGroup?.some((ecg:any) => storageColumnGroupArray.includes(ecg.evidenceColumnGroupId))
          ) {
            const displayedGroup = {
              dataPeriodId: evidencePeriodData.dataPeriodId,
              evidenceColumnGroup: evidencePeriodData?.evidenceColumnGroup?.map(
                (evidenceGroup:any) => {
                  return {
                    evidenceColumnGroupId: evidenceGroup.id,
                    show: true,
                  }
                })
            };
            const newEvidenceColumnGroup:any[] = 
                displayedGroup.evidenceColumnGroup
                .filter((displayECG:any) => !storageColumnGroupArray.includes(displayECG.evidenceColumnGroupId))
          
            if(newEvidenceColumnGroup.length) {
              storageGroup.evidenceColumnGroup.push(...newEvidenceColumnGroup)
            }
          } 

          if((storageGroup.dataPeriodId == evidencePeriodData.dataPeriodId)
          && evidencePeriodData?.evidenceColumnGroup.length
          && (evidencePeriodData?.evidenceColumnGroup.length < storageGroup.evidenceColumnGroup.length)){ //delete the assessment
            storageGroup = {
              ...storageGroup,
              evidenceColumnGroup:  [
                ...storageGroup.evidenceColumnGroup
                  .filter((storageECG: any) => evidencePeriodData.evidenceColumnGroup?.some((ecg: any) => storageECG.evidenceColumnGroupId == ecg.id) )
              ]
            }
          }
          return storageGroup;
        })]
      }
      
      localStorage.setItem(
        LocalStorageKeys.DisplayedGroups,
        JSON.stringify(storageGroups)
      )
      
      return Promise.resolve(storageGroups);
      }
  const getDisplayedGroupsFromStorage = (evidencePeriodData:any) => {
    if ((evidencePeriodData != undefined) && evidencePeriodData.evidenceColumnGroup.length > 0) {
      const displayedGroupsData = localStorage.getItem(
        LocalStorageKeys.DisplayedGroups
      );
      const storageGroups = displayedGroupsData
        ? JSON.parse(displayedGroupsData)
        : [];

        const existingStorageGroups = storageGroups.find(
          (displayedGroup: any) =>
          displayedGroup.dataPeriodId === evidencePeriodData.dataPeriodId
        );
        if ((existingStorageGroups != undefined) && existingStorageGroups.evidenceColumnGroup.length > 0) {
          return existingStorageGroups;
        } 
      localStorage.setItem(
        LocalStorageKeys.DisplayedGroups,
        JSON.stringify(storageGroups)
      );
      return storageGroups;
    }
  };

  const handleDisplayedGroupsChange = (
    dataPeriodId: number,
    evidenceColumnGroupId: number,
  ) => () => {
    let dataPeriodIdCheck: boolean;
    if((typeof displayedGroups == 'object') && (displayedGroups?.dataPeriodId === dataPeriodId)) {
      dataPeriodIdCheck = true;
    } else if(Array.isArray(displayedGroups)) {
      dataPeriodIdCheck = displayedGroups?.filter((dataGroup) => dataGroup.dataPeriodId === dataPeriodId).length > 0;
    } else {
      dataPeriodIdCheck = false;
    }
    if(Array.isArray(displayedGroups)) {
      displayedGroups?.map((displayedGroups) => {
        handleChangeFun(displayedGroups, dataPeriodId, evidenceColumnGroupId);
      })
    } else {
      handleChangeFun(displayedGroups, dataPeriodId, evidenceColumnGroupId);
    }
  };

  const handleChangeFun = (displayedGroups:any, dataPeriodId:any, evidenceColumnGroupId:any) => {
    if (displayedGroups?.dataPeriodId === dataPeriodId) {
      const displayedColumnsNum = displayedGroups?.evidenceColumnGroup.filter(
        (x:any) => x.show
      ).length;
      const displayedGroupData: DisplayedAssessementGroup = {
        ...displayedGroups,
        evidenceColumnGroup: displayedGroups?.evidenceColumnGroup.map(
          (evidenceColumnGroup:any) => {
            if (evidenceColumnGroup.evidenceColumnGroupId === evidenceColumnGroupId) {
              return {
                ...evidenceColumnGroup,
                show: !evidenceColumnGroup.show
                  ? !evidenceColumnGroup.show
                  : displayedColumnsNum > 1
                    ? !evidenceColumnGroup.show
                    : evidenceColumnGroup.show,
              };
            }
            return evidenceColumnGroup;
          }
        ),
      };

      const found =
      displayedGroupData.evidenceColumnGroup.filter(
          (ec) => ec.evidenceColumnGroupId === evidenceColumnGroupId
        ).length > 0;

      if (!found) {
        displayedGroupData.evidenceColumnGroup.push({
          evidenceColumnGroupId: evidenceColumnGroupId,
          show: true,
        });
      }

    
      setDisplayedGroup(displayedGroupData)
      const storageData = JSON.parse(
        localStorage.getItem(LocalStorageKeys.DisplayedGroups) || "[]"
      ) as Array<DisplayedAssessementGroup>;
      const mergedData = storageData
        .filter(
          (data) => displayedGroupData.dataPeriodId !== data.dataPeriodId
        )
        .concat(displayedGroupData);
      localStorage.setItem(
        LocalStorageKeys.DisplayedGroups,
        JSON.stringify(mergedData)
        );    
      }
  }

  const filterDisplayedGroups = (dataPeriodId: number, evidenceGroupId: number
  ) => {
    let col:any;
    if(Array.isArray(displayedGroups)) {
        const dataPeriodCol = (displayedGroups != undefined) && displayedGroups
        .find(
          (displayedColumn) => (displayedColumn != undefined) && (displayedColumn.dataPeriodId === dataPeriodId)
        );
        col = (dataPeriodCol?.evidenceColumns != undefined) && dataPeriodCol?.evidenceColumns.find((x:any) => x.evidenceColumnGroup === evidenceGroupId);

    } else if(typeof displayedGroups === 'object') {

      if(displayedGroups?.dataPeriodId === dataPeriodId){
        col = displayedGroups?.evidenceColumnGroup.find((x) => x.evidenceColumnGroupId === evidenceGroupId);
  
      }
    }
    
    if (col) {
      return col.show;
    } else {
      return true;
    }
  };

  const isChecked = (dataPeriodId:number, evidenceColumnGroupId: number) => {
    if(Array.isArray(displayedGroups)) {
      const group = displayedGroups?.find(
        (x) => x.dataPeriodId === dataPeriodId
      );
      if (group) {
        const col = group.evidenceColumnGroup.find(
          (x:any) => x.evidenceColumnGroupId === evidenceColumnGroupId
        );
        if (col) {
          return col.show;
        }
      }
    } else if(typeof displayedGroups === 'object') {
      const col = displayedGroups?.evidenceColumnGroup.find(
        (x:any) => x.evidenceColumnGroupId === evidenceColumnGroupId
      );
      if (col) {
        return col.show;
      }
    } 
     return false;
  };

  const getNumberDisplayingColumns = (): number => {
   if(displayedGroups !== undefined) {
      return displayedGroups?.evidenceColumnGroup?.filter((ec) => ec.show).length || 0;
    } else {
      return 0;
    }
  };
  return (
    <div>
      {/* {props.isLoading.getEvidenceData || props.isLoading.getDataPeriods ? (
        <div className="dataTableContainer">
          <LoadingIndicator evidenceDataTable/>
        </div>
      ) : (
        <> */}
          <div>
            {props.tableOnly && !isTeacher ? (
              <AddMoreData 
                className="inline mb-3" 
                getDataPeriodsByDistrict={() => window.location.reload()}
              />
            ) : (
              isDataPeriodOrEvidenceLoading
              ?  <div className="mb-3"> <LoadingIndicator /> </div>
              : props.hasEvidencePeriodData ? (
                <div className="mb-3">
                  <GroupStudents />
                </div>
              ) : null
            )}
            <div>
              <div className="dataTableContainer">
                <div>
                  <DataFiltersPanel 
                    evidenceGroupData = {evidencePeriodData}
                    numberDisplayedGroups={getNumberDisplayingColumns()}
                    onDisplayedGroupsChange={handleDisplayedGroupsChange}
                    checkDisplayedGroups={isChecked}
                    isDataPeriodLoading={ isDataPeriodOrEvidenceLoading }
                  />
                  {isDataPeriodOrEvidenceLoading
                  ? 
                    <LoadingIndicator type="assessment-table" evidenceDataTable rows={10} assessmentColumns={7} />
                  : 
                    <>
                      {props.hasEvidencePeriodData && 
                        <>
                            <EvidenceTable 
                              isPlanning 
                              filterDisplayedGroups = {filterDisplayedGroups}
                              isDataInterview = {true}
                            />
                        </>
                      }
                    </>
                  }
                </div>
              </div>
            </div>
          </div>
          {!isDataPeriodOrEvidenceLoading
           && <FormsEvidence />}
        {/* </>
      )} */}
      {props.hasEvidencePeriodData && <DataTablePagination/>}
      <FirstDataUploadTour />
      <PickDataSourceModal isDistrictOwner={isDistrictOwner}/>
    </div>
  );
};

const mapStateToProps = ({ onboarding, dataFilter }: ApplicationState): PropsFromState => {
  return {
    errors: {
      getDataPeriods: onboarding.errors.getDataPeriods,
      getEvidenceData: onboarding.errors.getEvidenceInDatePeriod,
      uploadAssessmentData: onboarding.errors.uploadAssessmentData,
      uploadUndefinedAssessmentData:
        onboarding.errors.uploadUndefinedAssessmentData,
      uploadEvidenceData: onboarding.errors.uploadEvidenceData,
      updateAssessment: onboarding.errors.updateAssessment,
      createAssessment: onboarding.errors.createAssessment,
    },
    isLoading: {
      getDataPeriods: onboarding.isLoading.getDataPeriods,
      getEvidenceData: onboarding.isLoading.getEvidenceInDatePeriod,
      uploadAssessmentData: onboarding.isLoading.uploadAssessmentData,
      uploadUndefinedAssessmentData:
        onboarding.isLoading.uploadUndefinedAssessmentData,
      uploadEvidenceData: onboarding.isLoading.uploadEvidenceData,
      updateDataPeriod: onboarding.isLoading.updateDataPeriod,
      deleteDataPeriod: onboarding.isLoading.deleteDataPeriod,
      updateAssessment: onboarding.isLoading.updateAssessment,
      createAssessment: onboarding.isLoading.createAssessment,
    },
    currentDataPeriod: onboarding.currentDataPeriod,
    isGetEvidenceDataPeriodAPIBlocked: onboarding.isGetEvidenceDataPeriodAPIBlocked,
    hasEvidencePeriodData:
      !!onboarding.evidencePeriodData &&
      onboarding.evidencePeriodData.evidence_column_groups.length > 0,  
  };
};

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps =>
  bindActionCreators(
    {
      getEvidenceInDataPeriod: getEvidenceInDataPeriod,
      getStudentInterventionGroups: getStudentInterventionGroups,
      getDataPeriods: getDataPeriods,
      getStaticDataCategoryDomains: getStaticDataCategoryDomains,
      getCustomBehaviors: getCustomBehaviors,
      hideGroupRecommendationFromData: hideGroupRecommendationFromData,
      resetPagination: resetPagination,
      resetFilterAction: resetFilterAction,
      getMasterDataInFilter: getMasterDataInFilter,
      resetEvidenceInDataPeriod: resetEvidenceInDataPeriod,
      resetSortAction: resetSortAction,
      resetGetEvidenceDataPeriodAPIBlocked: resetGetEvidenceDataPeriodAPIBlocked,
      openFileUploadErrorModal: openFileUploadErrorModal,
      getSkillFocuses: getSkillFocuses,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(EvidenceInfoTab);
